import {AuthApi} from "../api/AuthApi";
import {ApiError} from "../api/http";
import {MfaChallenge, MfaMethod, TokenSet} from "../api/models";
import {markSessionEnded} from "./sessionEnded";

// The signed-in session. The 30-day refresh token lives only in the API's HttpOnly cookie, which page scripts can't
// read. The 5-minute access token lives only in this tab's memory: a new page (or a tab that sees another tab sign
// in) gets one by renewing with the cookie. Each renewal replaces the cookie, so renewals are serialized across
// tabs (Web Locks); a renewal that waited sends whichever cookie is current by then.

const LEGACY_TOKENS_KEY = 'tsa-auth';
const SESSION_HINT_KEY = 'tsa-session';
const DEVICE_KEY = 'tsa-device-id';
const LOCK_NAME = 'tsa-auth-refresh';
const CHANNEL_NAME = 'tsa-auth';
const EXPIRY_MARGIN_SECONDS = 30;

export type SessionStatus = 'unknown' | 'signed-in' | 'signed-out';

export interface SessionUser {
    username: string;
    displayName: string;
    emailAddress: string;
    // Empty without an avatar; missing from tokens issued before avatars.
    avatarUrl?: string;
    claims: string[];
    // Mail to emailAddress bounces (SES reported it): SiteLayout asks the user to change it.
    emailUndeliverable?: boolean;
}

type Listener = () => void;
type ChannelMessage = 'signed-in' | 'signed-out';
const listeners = new Set<Listener>();

// Tokens stored by earlier versions of the site: readable by any script, so they're dropped (sign in again once).
try {
    localStorage.removeItem(LEGACY_TOKENS_KEY);
} catch {
    // Storage unavailable.
}

// Not a secret: only whether this browser has a session worth renewing, so signed-out visitors don't ask the API on
// every page load. Kept in memory too, for browsers that block storage.
let memoryHint = false;

function hasSessionHint(): boolean {
    try {
        return localStorage.getItem(SESSION_HINT_KEY) === '1' || memoryHint;
    } catch {
        return memoryHint;
    }
}

function setSessionHint(on: boolean): void {
    memoryHint = on;
    try {
        if (on) {
            localStorage.setItem(SESSION_HINT_KEY, '1');
        } else {
            localStorage.removeItem(SESSION_HINT_KEY);
        }
    } catch {
        // Storage unavailable: memoryHint covers this page.
    }
}

let accessToken: string | null = null;
// Bumped by every sign-out, so a renewal that was already under way when it happened is discarded.
let signOutGeneration = 0;
let status: SessionStatus = hasSessionHint() ? 'unknown' : 'signed-out';
let snapshotValue = `${status}:`;

function setState(token: string | null, nextStatus: SessionStatus): void {
    accessToken = token;
    status = nextStatus;
    snapshotValue = `${status}:${token ?? ''}`;
    listeners.forEach(x => x());
}

const channel: BroadcastChannel | null = typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL_NAME) : null;

function broadcast(message: ChannelMessage): void {
    channel?.postMessage(message);
}

function signedIn(result: TokenSet): void {
    setSessionHint(true);
    setState(result.accessToken, 'signed-in');
}

function signedOut(): void {
    signOutGeneration++;
    setSessionHint(false);
    setState(null, 'signed-out');
}

interface AccessTokenClaims {
    exp: number;
    payload: SessionUser;
}

function decode(token: string): AccessTokenClaims | null {
    try {
        const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const bytes = Uint8Array.from(atob(part), c => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
        return null;
    }
}

function isFresh(token: string): boolean {
    const claims = decode(token);
    return !!claims && claims.exp - EXPIRY_MARGIN_SECONDS > Date.now() / 1000;
}

async function withRefreshLock<T>(action: () => Promise<T>): Promise<T> {
    if (navigator.locks) {
        return navigator.locks.request(LOCK_NAME, action);
    }
    return action();
}

let pendingRefresh: Promise<string | null> | null = null;

export const tokenStore = {
    subscribe(listener: Listener): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },

    // A random id for this browser, which the API folds into the login identity's fingerprint.
    deviceId(): string {
        try {
            let id = localStorage.getItem(DEVICE_KEY);
            if (!id) {
                id = crypto.randomUUID();
                localStorage.setItem(DEVICE_KEY, id);
            }
            return id;
        } catch {
            return 'no-storage';
        }
    },

    // Changes whenever the session does (for useSyncExternalStore).
    snapshot(): string {
        return snapshotValue;
    },

    // 'unknown' while a new page finds out whether its session cookie is still good.
    status(): SessionStatus {
        return status;
    },

    user(): SessionUser | null {
        return accessToken ? decode(accessToken)?.payload ?? null : null;
    },

    // Null once signed in; a challenge when the account needs a second factor (see completeMfa).
    async login(username: string, password: string, turnstileToken: string): Promise<MfaChallenge | null> {
        const result = await AuthApi.login(username, password, this.deviceId(), turnstileToken);
        if ('mfaRequired' in result) {
            return result;
        }
        signedIn(result);
        broadcast('signed-in');
        return null;
    },

    async completeMfa(challenge: MfaChallenge, method: MfaMethod, code: string): Promise<void> {
        signedIn(await AuthApi.verifyMfa(challenge.mfaToken, method, code, this.deviceId()));
        broadcast('signed-in');
    },

    async logout(): Promise<void> {
        signedOut();
        broadcast('signed-out');
        // Ends the login identity and clears the cookie; signing out here doesn't depend on it. Under the renewal lock,
        // so it sends the current cookie rather than one a renewal is about to replace.
        await withRefreshLock(() => AuthApi.logout()).catch(() => undefined);
    },

    // An access token that's good for at least another 30 seconds, or null when signed out.
    async accessToken(): Promise<string | null> {
        if (accessToken && isFresh(accessToken)) {
            return accessToken;
        }
        if (status === 'signed-out') {
            return null;
        }
        return this.refresh(accessToken ?? undefined);
    },

    // Renews the session now, e.g. to pick up a changed email address.
    renew(): Promise<string | null> {
        return this.refresh(accessToken ?? undefined);
    },

    // Renews the session with the cookie. staleAccessToken is the token found to be expired or refused: if this tab
    // holds a different, fresh one by the time the lock is held, another call already renewed it.
    refresh(staleAccessToken?: string): Promise<string | null> {
        pendingRefresh ??= withRefreshLock(async () => {
            if (accessToken && accessToken !== staleAccessToken && isFresh(accessToken)) {
                return accessToken;
            }
            if (!hasSessionHint()) {
                if (status !== 'signed-out') {
                    setState(null, 'signed-out');
                }
                return null;
            }
            const generation = signOutGeneration;
            try {
                const result = await AuthApi.refresh(this.deviceId());
                if (generation !== signOutGeneration) {
                    return null; // signed out (here or in another tab) while this was under way
                }
                signedIn(result);
                return result.accessToken;
            } catch (err) {
                if (err instanceof ApiError && err.status !== 0 && err.status < 500) {
                    // The cookie is gone or no longer valid (the API has cleared it): signed out everywhere.
                    if (err.code === 'session_ended') {
                        markSessionEnded();
                    }
                    signedOut();
                    broadcast('signed-out');
                    return null;
                }
                if (status === 'unknown') {
                    // Couldn't reach the API: show signed out for now, but keep the hint so a reload tries again.
                    setState(null, 'signed-out');
                }
                throw err;
            }
        }).finally(() => {
            pendingRefresh = null;
        });
        return pendingRefresh;
    }
};

// Other tabs signing in or out. A tab that sees a sign-in fetches its own access token with the shared cookie.
function onOtherTab(message: ChannelMessage): void {
    if (message === 'signed-in') {
        if (status !== 'signed-in') {
            tokenStore.refresh().catch(() => undefined);
        }
    } else {
        signOutGeneration++;
        memoryHint = false;
        if (status !== 'signed-out') {
            setState(null, 'signed-out');
        }
    }
}

if (channel) {
    channel.onmessage = e => onOtherTab(e.data as ChannelMessage);
} else {
    // No BroadcastChannel: the session hint changing in storage says the same thing.
    window.addEventListener('storage', e => {
        if (e.key === SESSION_HINT_KEY) {
            onOtherTab(e.newValue === '1' ? 'signed-in' : 'signed-out');
        }
    });
}

// A new page with a session: get an access token from the cookie.
if (status === 'unknown') {
    tokenStore.refresh().catch(() => undefined);
}
